import { PDFDocument } from "pdf-lib";
import * as pdfjs from "pdfjs-dist";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const JPEG_QUALITY = 0.85;

function isAppleTouchDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /iP(hone|ad|od)/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1)
  );
}

function renderLimits() {
  if (isAppleTouchDevice()) return { scale: 1.5, maxEdge: 1536 };
  return { scale: 2, maxEdge: 2560 };
}

function asUint8Array(bytes) {
  if (bytes instanceof Uint8Array) return bytes;
  return new Uint8Array(bytes);
}

function isRecoverablePdfParseError(error) {
  return String(error?.message || "").includes("Failed to parse");
}

function fingerprint(data) {
  let hash = data.length >>> 0;
  const step = Math.max(1, Math.floor(data.length / 48));
  for (let i = 0; i < data.length; i += step) {
    hash = (Math.imul(hash, 33) + data[i]) >>> 0;
  }
  return `${data.length}:${hash}`;
}

function canvasToJpegBytes(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          reject(new Error("Could not rebuild a PDF page for editing."));
          return;
        }
        resolve(new Uint8Array(await blob.arrayBuffer()));
      },
      "image/jpeg",
      JPEG_QUALITY
    );
  });
}

async function rebuildPdfWithPdfJs(data) {
  const { scale: preferredScale, maxEdge } = renderLimits();
  const pdf = await pdfjs.getDocument({ data: data.slice(0) }).promise;
  const out = await PDFDocument.create();

  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const unit = page.getViewport({ scale: 1 });
      const longest = Math.max(unit.width, unit.height) || 1;
      const scale = Math.max(0.25, Math.min(preferredScale, maxEdge / longest));
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({
        canvasContext: ctx,
        viewport,
        background: "rgb(255,255,255)",
      }).promise;

      const jpg = await canvasToJpegBytes(canvas);
      const image = await out.embedJpg(jpg);
      const pdfPage = out.addPage([unit.width, unit.height]);
      pdfPage.drawImage(image, {
        x: 0,
        y: 0,
        width: unit.width,
        height: unit.height,
      });
      canvas.width = 0;
      canvas.height = 0;
      page.cleanup();
    }
  } finally {
    try {
      await pdf.destroy();
    } catch {
      pdf.cleanup?.();
    }
  }

  return out.save({ useObjectStreams: false });
}

let repairCache = null;

async function loadRepairedPdf(data) {
  const key = fingerprint(data);
  if (!repairCache || repairCache.key !== key) {
    repairCache = { key, bytes: await rebuildPdfWithPdfJs(data) };
  }
  return PDFDocument.load(repairCache.bytes.slice(0), { ignoreEncryption: true });
}

export async function loadPdfForEdit(bytes) {
  const data = asUint8Array(bytes);
  try {
    const doc = await PDFDocument.load(data, { ignoreEncryption: true });
    repairCache = null;
    return doc;
  } catch (error) {
    if (!isRecoverablePdfParseError(error)) throw error;
    try {
      return await loadRepairedPdf(data);
    } catch (rebuildError) {
      const failed = new Error(
        rebuildError?.message ||
          "This PDF could not be edited. Open it, re-save a copy, and try again."
      );
      failed.cause = error;
      throw failed;
    }
  }
}
